import React, { useState, useEffect } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import SurveyEmbed from './SurveyEmbed';
import { getStore } from '../ApplicationState';
import { getUserProfileFromDB, isStudent } from '../UserManagementHelpers';
import { setUserProfile } from '../UserManagement';
import { setLocalStorage } from '../aaa';
import { createSurveySession } from '../BackendInterface';
import { ShowSurveyParameters } from '../../peekapak-types/SurveyCampaignTypes';
import { safeSessionStorage } from '../safeStorage';
import { incrementSurveySequenceCount, resetSurveySequenceCount, clearUserChoice } from '../SurveyChoiceHelpers';
import Analytics from '../Analytics';
import { SurveyChainDetectionData } from '../SurveyPromptDecisionService';

// Session storage helpers for managing active survey sessions
interface StoredSurveySession {
  sessionId: string;
  surveyParameters: ShowSurveyParameters;
  createdAt: number;
}

const getActiveSession = (campaignId: string): StoredSurveySession | null => {
  if (!campaignId) return null;

  try {
    const stored = safeSessionStorage.getItem(`surveySession_${campaignId}`);
    if (stored) {
      const session: StoredSurveySession = JSON.parse(stored);
      // Check if session is still valid (24 hours)
      if (Date.now() - session.createdAt < 24 * 60 * 60 * 1000) {
        return session;
      }
      // Clean up expired session
      safeSessionStorage.removeItem(`surveySession_${campaignId}`);
    }
  } catch (error) {
    console.error('Error reading active session:', error);
  }
  return null;
};

const saveActiveSession = (campaignId: string, sessionId: string, surveyParameters: ShowSurveyParameters) => {
  if (!campaignId || !sessionId) return;

  try {
    const sessionData: StoredSurveySession = {
      sessionId,
      surveyParameters,
      createdAt: Date.now(),
    };
    safeSessionStorage.setItem(`surveySession_${campaignId}`, JSON.stringify(sessionData));
  } catch (error) {
    console.error('Error saving active session:', error);
  }
};

const clearActiveSession = (campaignId: string) => {
  if (!campaignId) return;

  try {
    safeSessionStorage.removeItem(`surveySession_${campaignId}`);
  } catch (error) {
    console.error('Error clearing active session:', error);
  }
};

const TypeformPage: React.FC = () => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [sessionId, setSessionId] = useState('');
  const [isLoadingSession, setIsLoadingSession] = useState(true);
  const [sessionError, setSessionError] = useState<string | null>(null);
  const [isLoadingNext, setIsLoadingNext] = useState(false);
  const [isProcessingSubmission, setIsProcessingSubmission] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const maxRetries = 30; // Will attempt 6 total checks (initial + 5 retries)
  const location = useLocation<{
    nextPathname?: string;
    surveyParameters?: ShowSurveyParameters;
  }>();
  const history = useHistory();

  const { userProfile } = getStore().getState().user;

  // Check for stored next survey parameters from a chain navigation
  const storedNextSurvey = (() => {
    try {
      const stored = safeSessionStorage.getItem('nextSurveyParameters');
      if (stored) {
        const params = JSON.parse(stored);
        // Clear it immediately after reading to prevent reuse
        safeSessionStorage.removeItem('nextSurveyParameters');
        return params;
      }
    } catch (error) {
      console.error('Error reading stored survey parameters:', error);
    }
    return null;
  })();

  // Get survey parameters from stored chain, location state, user profile, or active session
  const surveyParams: ShowSurveyParameters = (() => {
    // First priority: stored parameters from survey chain
    if (storedNextSurvey?.surveyParameters) {
      // Also update location state for consistency
      if (
        !location.state?.surveyParameters ||
        location.state.surveyParameters.campaignId !== storedNextSurvey.surveyParameters.campaignId
      ) {
        history.replace(location.pathname, {
          surveyParameters: storedNextSurvey.surveyParameters,
          nextPathname: storedNextSurvey.nextPathname,
        });
      }
      return storedNextSurvey.surveyParameters;
    }

    const locationCampaignId = location.state?.surveyParameters?.campaignId;
    const profileCampaignId = userProfile?.surveyParameters?.campaignId;

    if (locationCampaignId === profileCampaignId) {
      // Location state is current, use it (preserves navigation context)
      return location.state.surveyParameters;
    }

    // Check if we have an active session with survey parameters
    if (profileCampaignId) {
      const activeSession = getActiveSession(profileCampaignId);
      if (activeSession && activeSession.surveyParameters) {
        return activeSession.surveyParameters;
      }
    }

    // Location state is stale or missing, use profile
    return userProfile?.surveyParameters || ({} as ShowSurveyParameters);
  })();

  const typeformId = surveyParams.formId;

  // Get user profile to extract district and schoolId

  // Helper function to map grade to instruction level
  const getInstructionLevel = (grade: number): string => {
    if (grade === -1) return 'PreKindergarten';
    if (grade === 0) return 'Kindergarten';
    if (grade >= 1 && grade <= 12) {
      const suffix = grade === 1 ? 'st' : grade === 2 ? 'nd' : grade === 3 ? 'rd' : 'th';
      return `${grade}${suffix} Grade`;
    }
    if (grade === undefined || grade === null) {
      return 'NA';
    }
    return `Grade ${grade}`; // Fallback for unexpected grades
  };

  // Create survey session when component mounts
  useEffect(() => {
    const initializeSurveySession = async () => {
      try {
        setIsLoadingSession(true);
        setSessionError(null);

        // Check for existing session for this campaign
        const existingSession = getActiveSession(surveyParams.campaignId);

        if (existingSession) {
          console.log('Reusing existing session for campaign:', surveyParams.campaignId, existingSession.sessionId);
          setSessionId(existingSession.sessionId);
          return;
        }

        // Create new session only if none exists
        console.log('Creating new session for campaign:', surveyParams.campaignId);
        const response = await createSurveySession(surveyParams);
        setSessionId(response.sessionId);
        saveActiveSession(surveyParams.campaignId, response.sessionId, surveyParams);
      } catch (error) {
        console.error('Error creating survey session:', error);
        setSessionError('Failed to initialize survey session. Please try again.');
      } finally {
        setIsLoadingSession(false);
      }
    };

    if (surveyParams.campaignId && surveyParams.formId) {
      initializeSurveySession();
    } else {
      setIsLoadingSession(false);
    }
  }, [surveyParams.campaignId, surveyParams.formId]);

  const handleSubmit = async () => {
    setIsSubmitted(true);
    setIsProcessingSubmission(true);

    // Set a flag to prevent redirects during survey completion processing
    setLocalStorage('surveyCompletionInProgress', 'true', 1); // 1 minute expiry

    // Clear active session for this campaign since survey is completed
    const campaignId = surveyParams.campaignId;
    if (campaignId && userProfile) {
      clearActiveSession(campaignId);

      // Note: No need to track completion in localStorage
      // Backend will update user profile with completion history
      // and re-compute eligibility for next survey
    }

    // Log the session that was submitted
    console.log(`Form submitted with session ID: ${sessionId}, campaign ID: ${campaignId}`);

    // Wait for backend to process the completion and update campaign status
    // This gives the backend time to mark the current campaign as complete
    // before we check for the next survey in the chain
    const submissionStartTime = Date.now();
    console.log('Waiting 3 seconds for initial backend processing...');
    await new Promise((resolve) => setTimeout(resolve, 3000));

    // Refresh user profile to check for next survey with retry logic
    try {
      // Use cache busting to ensure we get fresh data (not cached by API Gateway)
      let currentProfile = await getUserProfileFromDB(true);
      getStore().dispatch(setUserProfile(currentProfile));

      // Keep isProcessingSubmission true during retry loop
      // Will be set to false when navigation happens or retry loop completes

      // Retry loop: Try up to 6 times (initial + 5 retries) with 2s intervals
      let attemptNumber = 1;
      const maxAttempts = maxRetries + 1; // 6 total attempts
      const retryDelayMs = 2000; // 2 seconds between retries
      let chainDetectionSuccess = false;
      let newCampaignDetected = false;

      while (attemptNumber <= maxAttempts) {
        const isNewCampaign = currentProfile.surveyParameters?.campaignId !== surveyParams.campaignId;
        const hasMoreSurveys = currentProfile.flags?.isShowSurvey && !!currentProfile.surveyParameters;

        console.log(`Survey chain detection attempt ${attemptNumber}/${maxAttempts}:`, {
          oldCampaignId: surveyParams.campaignId,
          newCampaignId: currentProfile.surveyParameters?.campaignId,
          isNewCampaign,
          hasMoreSurveys,
          hasIsShowSurvey: currentProfile.flags?.isShowSurvey,
        });

        // SUCCESS: Different campaign detected
        if (isNewCampaign && hasMoreSurveys) {
          console.log(
            `New campaign detected on attempt ${attemptNumber}:`,
            currentProfile.surveyParameters?.campaignId,
          );
          chainDetectionSuccess = true;
          newCampaignDetected = true;
          setIsProcessingSubmission(false); // Stop showing retry message
          setIsLoadingNext(true);

          // Clear any stale choice for the NEW campaign
          if (currentProfile.surveyParameters && userProfile) {
            const newCampaignId = currentProfile.surveyParameters.campaignId;
            clearUserChoice(userProfile.userId, newCampaignId);
          }

          // Log chain detection success to telemetry
          if (userProfile) {
            const telemetryData: SurveyChainDetectionData = {
              success: true,
              attemptsNeeded: attemptNumber,
              totalTimeMs: Date.now() - submissionStartTime,
              oldCampaignId: surveyParams.campaignId,
              newCampaignId: currentProfile.surveyParameters?.campaignId,
              backendHadMoreSurveys: true,
              maxRetriesReached: false,
            };

            console.log('[Survey Chain Detection] Success:', telemetryData);

            Analytics.telemetry('surveyChainDetection', [telemetryData], userProfile).catch((error) => {
              console.error('[Survey Chain Detection] Analytics logging failed:', error);
            });
          }

          // Navigate to survey choice for the next survey
          setTimeout(() => {
            if (userProfile) {
              incrementSurveySequenceCount(userProfile.userId);
            }
            setLocalStorage('surveyCompletionInProgress', 'false', 1);
            safeSessionStorage.setItem(
              'nextSurveyParameters',
              JSON.stringify({
                surveyParameters: currentProfile.surveyParameters,
                nextPathname: location.state?.nextPathname || '/',
              }),
            );
            window.location.href = '/survey-choice';
          }, 500);

          return; // Exit function - success path
        }

        // DONE: Backend says no more surveys
        if (!hasMoreSurveys) {
          console.log(`Backend indicates no more surveys (attempt ${attemptNumber})`);
          break; // Exit retry loop
        }

        // RETRY: Same campaign, backend hasn't updated yet
        if (attemptNumber < maxAttempts) {
          console.log(`Same campaign on attempt ${attemptNumber}, retrying in ${retryDelayMs}ms...`);

          // Update UI to show retry progress
          setRetryCount(attemptNumber);

          // Wait before next attempt
          await new Promise((resolve) => setTimeout(resolve, retryDelayMs));

          // Fetch fresh profile for next attempt with cache busting
          try {
            currentProfile = await getUserProfileFromDB(true);
            getStore().dispatch(setUserProfile(currentProfile));
          } catch (error) {
            console.error(`Error fetching profile on attempt ${attemptNumber}:`, error);
            // Continue to next attempt if not at max
          }

          attemptNumber++;
        } else {
          // MAX RETRIES: Backend too slow or truly no new survey
          console.log(`Max retries (${maxAttempts}) reached without detecting new campaign`);
          break; // Exit retry loop
        }
      }

      // If we get here: No new campaign after retries, or no more surveys

      // Stop showing retry UI
      setIsProcessingSubmission(false);

      // Log chain detection result to telemetry
      if (userProfile) {
        const telemetryData: SurveyChainDetectionData = {
          success: chainDetectionSuccess,
          attemptsNeeded: attemptNumber,
          totalTimeMs: Date.now() - submissionStartTime,
          oldCampaignId: surveyParams.campaignId,
          newCampaignId: currentProfile.surveyParameters?.campaignId,
          backendHadMoreSurveys: currentProfile.flags?.isShowSurvey || false,
          maxRetriesReached: attemptNumber >= maxAttempts && !newCampaignDetected,
        };

        console.log('[Survey Chain Detection] Complete:', telemetryData);

        Analytics.telemetry('surveyChainDetection', [telemetryData], userProfile).catch((error) => {
          console.error('[Survey Chain Detection] Analytics logging failed:', error);
        });
      }

      // All surveys complete or max retries reached - redirect to home
      console.log('Survey chain complete, redirecting to home');
      const nextPath = location.state?.nextPathname || '/';

      setTimeout(() => {
        if (userProfile) {
          resetSurveySequenceCount(userProfile.userId);
        }
        setLocalStorage('surveyCompletionInProgress', 'false', 1);
        setRetryCount(0); // Reset UI state
        history.push(nextPath);
      }, 500);
    } catch (error) {
      console.error('Error during survey chain check:', error);

      // Reset state on error
      setIsProcessingSubmission(false);
      setRetryCount(0);

      // Clear processing lock
      setLocalStorage('surveyCompletionInProgress', 'false', 1);

      // Redirect to home on error
      const nextPath = location.state?.nextPathname || '/';
      setTimeout(() => {
        if (userProfile) {
          resetSurveySequenceCount(userProfile.userId);
        }
        history.push(nextPath);
      }, 3000);
    }
  };

  return (
    <div
      className='bg-gray-50 px-4 sm:px-6 lg:px-8 flex items-start justify-center'
      style={{ minHeight: 'calc(100vh - 80px)' }}
    >
      <div className='max-w-4xl w-full py-8 flex flex-col' style={{ minHeight: 'calc(100vh - 80px - 4rem)' }}>
        {isSubmitted ? (
          <div className='bg-white shadow-lg rounded-lg p-8 text-center'>
            {isProcessingSubmission && retryCount === 0 ? (
              <>
                <div className='animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto'></div>
                <h2 className='mt-3 text-lg font-medium text-gray-900'>Processing your submission...</h2>
                <p className='mt-2 text-gray-500'>Please wait while we save your responses.</p>
              </>
            ) : isProcessingSubmission && retryCount > 0 ? (
              <>
                <div className='animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500 mx-auto'></div>
                <h2 className='mt-3 text-lg font-medium text-gray-900'>Checking for more surveys...</h2>
                <div className='mt-4 w-full max-w-xs mx-auto'>
                  <div className='bg-gray-200 rounded-full h-2 overflow-hidden'>
                    <div
                      className='bg-purple-500 h-full transition-all duration-500 ease-out'
                      style={{ width: `${(retryCount / maxRetries) * 100}%` }}
                    ></div>
                  </div>
                  <p className='mt-2 text-sm text-gray-400 text-center'>This may take a few moments...</p>
                </div>
              </>
            ) : isLoadingNext ? (
              <>
                <div className='animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-500 mx-auto'></div>
                <h2 className='mt-3 text-lg font-medium text-gray-900'>Great work!</h2>
                <p className='mt-2 text-gray-500'>Loading your next survey...</p>
              </>
            ) : (
              <>
                <svg
                  className='mx-auto text-green-600'
                  width='64'
                  height='64'
                  fill='none'
                  viewBox='0 0 24 24'
                  stroke='currentColor'
                >
                  <circle cx='12' cy='12' r='11' fill='#D1FAE5' />
                  <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M5 13l4 4L19 7' />
                </svg>
                <h2 className='mt-3 text-lg font-medium text-gray-900'>Thank you for participating in our survey.</h2>
                <p className='mt-2 text-gray-500'>Your responses have been recorded. We appreciate your time.</p>
              </>
            )}
          </div>
        ) : isLoadingSession ? (
          <div className='bg-white shadow-lg rounded-lg p-8 text-center'>
            <div className='animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto'></div>
            <h2 className='mt-3 text-lg font-medium text-gray-900'>Preparing your survey...</h2>
            <p className='mt-2 text-gray-500'>Please wait while we set up your survey session.</p>
          </div>
        ) : sessionError ? (
          <div className='bg-white shadow-lg rounded-lg p-8 text-center'>
            <div className='mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100'>
              <svg className='h-6 w-6 text-red-600' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
                <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M6 18L18 6M6 6l12 12' />
              </svg>
            </div>
            <h2 className='mt-3 text-lg font-medium text-gray-900'>Survey Initialization Error</h2>
            <p className='mt-2 text-gray-500'>{sessionError}</p>
          </div>
        ) : (
          <div className='bg-white shadow-lg rounded-lg overflow-hidden flex-1 flex flex-col'>
            <div className='px-4 py-5 sm:p-6 flex-1 flex flex-col'>
              <SurveyEmbed
                formId={typeformId}
                minHeight='calc(100vh - 200px)'
                onSubmit={handleSubmit}
                className='rounded-lg'
                hideHeaders={true}
                hideFooter={false}
                hiddenFields={{
                  session_id: sessionId,
                  campaign_id: surveyParams.campaignId || '',
                  district: userProfile?.district || '',
                  school_id: userProfile?.schoolId || '',
                  ...(userProfile &&
                    isStudent(userProfile) && {
                      instruction_level: getInstructionLevel(userProfile.grade),
                    }),
                  survey_domain: surveyParams.surveyDomain,
                  respondent_group: surveyParams.respondentGroup,
                  timestamp: Date.now().toString(),
                  source: 'embedded-form',
                  nationality: userProfile?.nationality || 'unknown',
                  ethnicity: userProfile?.ethnicity || 'unknown',
                  gender: userProfile?.gender || 'unknown',
                  birthdate: userProfile?.birthdate || 'unknown',
                  socio_economic_status: userProfile?.socioEconomicStatus || 'unknown',
                  iep: userProfile?.iep || 'unknown',
                }}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TypeformPage;
